import { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Search, Edit2, Check, X } from 'lucide-react';

export default function SuperAdminUsers() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});

  const load = async (q = search) => {
    setLoading(true);
    try {
      const data = await api.get(`/superadmin/users?search=${q}`);
      setUsers(data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    clearTimeout(window._userSearchTimer);
    window._userSearchTimer = setTimeout(() => load(e.target.value), 400);
  };

  const startEdit = (user) => {
    setEditingId(user.id);
    setEditData({ role: user.role, isActive: user.isActive, name: user.name, password: '' });
  };

  const saveEdit = async (userId) => {
    try {
      const payload = { role: editData.role, isActive: editData.isActive, name: editData.name };
      if (editData.password) payload.password = editData.password;
      const updated = await api.put(`/superadmin/users/${userId}`, payload);
      setUsers(users.map(u => u.id === userId ? { ...u, ...updated } : u));
      setEditingId(null);
      toast.success('Kullanıcı güncellendi');
    } catch { toast.error('Güncellenemedi'); }
  };

  const roleColor = { SUPERADMIN: '#f59e0b', ADMIN: '#3b82f6', RECEPTIONIST: '#10b981', VIEWER: '#94a3b8' };

  return (
    <div style={{ padding: 28 }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700 }}>Kullanıcılar</h1>
        <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>{users.length} kullanıcı</div>
      </div>

      <div style={{ position: 'relative', marginBottom: 20 }}>
        <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
        <input value={search} onChange={handleSearch} placeholder="İsim veya email ara..."
          style={{ paddingLeft: 36, width: '100%', maxWidth: 400 }} />
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>Yükleniyor...</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                {['Kullanıcı', 'Otel', 'Rol', 'Durum', 'Son Giriş', 'İşlem'].map(h => (
                  <th key={h} style={{ padding: '10px 16px', fontSize: 12, fontWeight: 600, color: '#64748b', textAlign: 'left' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '12px 16px' }}>
                    {editingId === user.id ? (
                      <input value={editData.name} onChange={e => setEditData({ ...editData, name: e.target.value })} style={{ fontSize: 13, padding: '4px 8px' }} />
                    ) : (
                      <>
                        <div style={{ fontWeight: 500, fontSize: 14 }}>{user.name}</div>
                        <div style={{ fontSize: 12, color: '#94a3b8' }}>{user.email}</div>
                      </>
                    )}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: '#475569' }}>
                    {user.hotel?.name || <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>—</span>}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    {editingId === user.id ? (
                      <select value={editData.role} onChange={e => setEditData({ ...editData, role: e.target.value })} style={{ fontSize: 12, padding: '4px 8px' }}>
                        <option value="ADMIN">ADMIN</option>
                        <option value="RECEPTIONIST">RECEPTIONIST</option>
                        <option value="VIEWER">VIEWER</option>
                        <option value="SUPERADMIN">SUPERADMIN</option>
                      </select>
                    ) : (
                      <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 20, background: (roleColor[user.role] || '#94a3b8') + '18', color: roleColor[user.role] || '#94a3b8' }}>
                        {user.role}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    {editingId === user.id ? (
                      <select value={editData.isActive ? 'true' : 'false'} onChange={e => setEditData({ ...editData, isActive: e.target.value === 'true' })} style={{ fontSize: 12, padding: '4px 8px' }}>
                        <option value="true">Aktif</option>
                        <option value="false">Pasif</option>
                      </select>
                    ) : (
                      <span style={{ fontSize: 12, color: user.isActive ? '#16a34a' : '#ef4444', fontWeight: 600 }}>
                        {user.isActive ? 'Aktif' : 'Pasif'}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 12, color: '#94a3b8' }}>
                    {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString('tr-TR') : '—'}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    {editingId === user.id ? (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <input value={editData.password} onChange={e => setEditData({ ...editData, password: e.target.value })}
                          placeholder="Yeni şifre (opsiyonel)" type="password" style={{ fontSize: 12, padding: '4px 8px', width: 140 }} />
                        <button onClick={() => saveEdit(user.id)} style={{ background: '#22c55e', border: 'none', borderRadius: 6, padding: '5px 8px', cursor: 'pointer', color: 'white', display: 'flex' }}>
                          <Check size={14} />
                        </button>
                        <button onClick={() => setEditingId(null)} style={{ background: '#e2e8f0', border: 'none', borderRadius: 6, padding: '5px 8px', cursor: 'pointer', display: 'flex' }}>
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <button onClick={() => startEdit(user)} style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#475569' }}>
                        <Edit2 size={13} /> Düzenle
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
